import { Palmtree, Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { useSaveVacation, useUsers, useVacations, type Vacation, type VacationItem } from "@/api/queries";
import { isStaff, useAuth, useCurrentUser } from "@/app/auth";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { UserAvatar } from "@/components/common/UserAvatar";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useScopeTeams } from "@/features/activity/TeamSelect";
import { errorText } from "@/lib/errors";

import { useVacationText } from "./useVacationText";
import { VacationDialog } from "./VacationDialog";

type Person = { id: string; full_name: string };
type Action =
  | { kind: "edit"; person?: Person; initial?: Vacation }
  | { kind: "pick" }
  | { kind: "clear"; person: Person };

/** Who is on vacation now and soon; marks are set by the employee, their manager or an administrator. */
export function VacationsPage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const staff = isStaff(me);
  const list = useVacations();
  const users = useUsers();
  const teams = useScopeTeams();
  const [action, setAction] = useState<Action | null>(null);
  const items = list.data;
  const current = items?.filter((i) => i.vacation.active) ?? [];
  const upcoming = items?.filter((i) => !i.vacation.active) ?? [];

  // Administrators mark anyone; managers the members of the teams they manage.
  const managed = new Set(teams.flatMap((team) => team.members.map((m) => m.user_id)));
  const people = (users.data ?? [])
    .filter((u) => u.status === "active" && u.id !== me.id && (me.role === "admin" || managed.has(u.id)))
    .map((u) => ({ id: u.id, full_name: u.full_name }));

  return (
    <>
      <PageHeader
        title={t("nav.timeOff")}
        description={t("vacations.subtitle")}
        actions={
          staff &&
          people.length > 0 && (
            <Button variant="secondary" onClick={() => setAction({ kind: "pick" })}>
              <Plus /> {t("vacations.markEmployee")}
            </Button>
          )
        }
      />
      <div className="flex flex-col gap-6">
        <MyVacation
          onEdit={(initial) => setAction({ kind: "edit", initial })}
          onClear={(person) => setAction({ kind: "clear", person })}
        />

        <Section title={t("vacations.now")} description={t("vacations.nowHint")}>
          {!items ? (
            <Skeleton className="m-5 h-12" />
          ) : current.length === 0 ? (
            <EmptyState icon={Palmtree} title={t("vacations.nobody")} />
          ) : (
            <VacationRows items={current} onAction={setAction} />
          )}
        </Section>
        {upcoming.length > 0 && (
          <Section title={t("vacations.upcoming")}>
            <VacationRows items={upcoming} onAction={setAction} />
          </Section>
        )}
      </div>

      {action?.kind === "edit" && (
        <VacationDialog
          person={action.person ?? { id: me.id, full_name: me.full_name }}
          initial={action.initial}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "pick" && <VacationDialog people={people} onClose={() => setAction(null)} />}
      {action?.kind === "clear" && <ClearDialog person={action.person} onClose={() => setAction(null)} />}
    </>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      {children}
    </Card>
  );
}

function MyVacation({
  onEdit,
  onClear,
}: {
  onEdit: (initial?: Vacation) => void;
  onClear: (person: Person) => void;
}) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const text = useVacationText();
  const v = me.vacation;
  return (
    <Card className="flex flex-wrap items-center gap-4 p-5">
      <span className="flex size-10 items-center justify-center rounded-lg bg-primary-soft text-primary">
        <Palmtree className="size-5" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-semibold">{t("vacations.mine")}</div>
        <div className="text-muted">
          {!v
            ? t("vacations.mineNone")
            : v.active
              ? t("vacations.mineActive", { range: text(v) })
              : t("vacations.minePlanned", { range: text(v) })}
        </div>
      </div>
      <div className="flex gap-2">
        {v && (
          <Button variant="secondary" onClick={() => onClear({ id: me.id, full_name: me.full_name })}>
            {t("vacations.clear")}
          </Button>
        )}
        <Button onClick={() => onEdit(v)}>{v ? t("vacations.change") : t("vacations.markMine")}</Button>
      </div>
    </Card>
  );
}

function VacationRows({ items, onAction }: { items: VacationItem[]; onAction: (a: Action) => void }) {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const text = useVacationText();
  return (
    <ul>
      {items.map((item) => (
        <li
          key={item.user.id}
          className="flex flex-wrap items-center gap-3 border-t px-5 py-3 first:border-t-0"
        >
          <UserAvatar name={item.user.full_name} className="size-8" />
          <div className="min-w-0 flex-1">
            <div className="font-medium">
              {item.user.full_name}
              {item.user.id === me.id && <span className="text-muted"> · {t("vacations.you")}</span>}
            </div>
            <div className="text-muted">{text(item.vacation)}</div>
          </div>
          {item.can_edit && (
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAction({ kind: "edit", person: item.user, initial: item.vacation })}
              >
                {t("vacations.change")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onAction({ kind: "clear", person: item.user })}
              >
                {t("vacations.clear")}
              </Button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function ClearDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const { t } = useTranslation();
  const auth = useAuth();
  const save = useSaveVacation();
  return (
    <ConfirmDialog
      title={t("vacations.clearTitle")}
      description={t("vacations.clearDescription", { name: person.full_name })}
      confirmLabel={t("vacations.clear")}
      danger
      errorText={(err) => errorText(t, err)}
      onConfirm={async () => {
        const user = await save.mutateAsync({ userId: person.id });
        if (auth.status === "authenticated" && user.id === auth.me.user.id) auth.setMe({ ...auth.me, user });
      }}
      onClose={onClose}
    />
  );
}
